/**
 * useDiscoverQuery - React Query-based discover hook
 *
 * Phase 5G: Replaces useMovies with React Query for cache-first + SWR behavior.
 *
 * Features:
 * - useInfiniteQuery for paginated discover/search
 * - Cache-first with background revalidation
 * - Persisted cache for instant hard refresh (default filters only)
 * - Client-side filtering/sorting in useMemo
 * - Normalized query keys (sorted arrays) to prevent key explosion
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { MediaItem, Filters, SortOption, MediaType } from '../types/movie';
import { discoverMedia, searchMedia, type MediaDiscoverResponse } from '../api/tmdb';
import { DEFAULT_FILTERS } from '../utils/constants';
import { calculateOurRating } from '../utils/helpers';
import { queryKeys, type DiscoverParams } from '../lib/queryKeys';

// ============================================================================
// Types
// ============================================================================

interface UseDiscoverQueryResult {
  movies: MediaItem[];
  loading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  error: string | null;
  hasMore: boolean;
  totalResults: number;
  loadMore: () => void;
  filters: Filters;
  setFilters: (filters: Filters) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

// Sort options that require client-side sorting
const CLIENT_SORT_OPTIONS: SortOption[] = [
  'our_rating.desc', 'our_rating.asc',
  'runtime.desc', 'runtime.asc',
];

// ============================================================================
// Helpers
// ============================================================================

/**
 * Extract and NORMALIZE API-relevant params from full Filters object.
 * Arrays are sorted to ensure stable query keys (prevents key explosion).
 */
function extractDiscoverParams(filters: Filters): DiscoverParams {
  return {
    // Sort arrays for stable keys
    genres: [...filters.genres].sort((a, b) => a - b),
    languages: [...filters.languages].sort(),
    actorSearch: [...filters.actorSearch].sort(),
    directorSearch: [...filters.directorSearch].sort(),
    // Tuples and primitives are already stable
    yearRange: filters.yearRange,
    runtimeRange: filters.runtimeRange,
    sortBy: filters.sortBy,
    popularOnly: filters.popularOnly,
    releasedOnly: filters.releasedOnly,
    ratingRange: filters.ratingRange,
    rtRatingRange: filters.rtRatingRange,
    tmdbRatingRange: filters.tmdbRatingRange,
    ourRatingRange: filters.ourRatingRange,
  };
}

/**
 * Sort media items client-side.
 */
function sortMedia(items: MediaItem[], sortBy: SortOption): MediaItem[] {
  const [field, order] = sortBy.split('.') as [string, 'asc' | 'desc'];

  return [...items].sort((a, b) => {
    let aVal = 0;
    let bVal = 0;

    switch (field) {
      case 'our_rating':
        aVal = calculateOurRating(a.imdb_rating, a.rt_rating) || 0;
        bVal = calculateOurRating(b.imdb_rating, b.rt_rating) || 0;
        break;
      case 'runtime':
        // Runtime sorting uses whatever runtime is in the API payload
        // Items without runtime sort to the end
        aVal = a.runtime || 0;
        bVal = b.runtime || 0;
        break;
      default:
        return 0;
    }

    return order === 'desc' ? bVal - aVal : aVal - bVal;
  });
}

/**
 * Apply client-side filters to items.
 */
function applyClientFilters(items: MediaItem[], filters: Filters): MediaItem[] {
  let filtered = items;

  // Runtime filter - only filter items that have runtime data
  if (filters.runtimeRange[0] > 0 || filters.runtimeRange[1] < 300) {
    filtered = filtered.filter(item => {
      // Keep items without runtime (they pass through)
      if (!item.runtime) return true;
      return item.runtime >= filters.runtimeRange[0] && item.runtime <= filters.runtimeRange[1];
    });
  }

  // IMDb rating filter
  if (filters.ratingRange[0] > 0 || filters.ratingRange[1] < 10) {
    filtered = filtered.filter(item => {
      if (!item.imdb_rating) return false;
      return item.imdb_rating >= filters.ratingRange[0] && item.imdb_rating <= filters.ratingRange[1];
    });
  }

  // RT rating filter
  if (filters.rtRatingRange[0] > 0 || filters.rtRatingRange[1] < 100) {
    filtered = filtered.filter(item => {
      if (!item.rt_rating) return false;
      return item.rt_rating >= filters.rtRatingRange[0] && item.rt_rating <= filters.rtRatingRange[1];
    });
  }

  // Filmpl rating: server-side filtered via filmpl_rating.gte/lte (no client filter needed)

  return filtered;
}

// ============================================================================
// Hook
// ============================================================================

export function useDiscoverQuery(
  initialFilters?: Partial<Filters>,
  mediaType: MediaType = 'movie',
  initialSearchQuery: string = ''
): UseDiscoverQueryResult {
  // Local state for filters and search (controlled by FilmFinder)
  const [filters, setFilters] = useState<Filters>({
    ...(DEFAULT_FILTERS as Filters),
    ...initialFilters,
  });
  // Initialize searchQuery from URL param to prevent double-fetch on hard refresh with ?q=
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);

  // Track media type changes
  const prevMediaTypeRef = useRef(mediaType);
  const isInitialMountRef = useRef(true);

  // Extract and normalize API params for query key (sorted arrays for stability)
  const discoverParams = useMemo(() => extractDiscoverParams(filters), [filters]);

  // Prepare API filters (use popularity.desc for client-sorted options)
  const apiFilters = useMemo(() => {
    if (CLIENT_SORT_OPTIONS.includes(filters.sortBy)) {
      return { ...filters, sortBy: 'popularity.desc' as SortOption };
    }
    return filters;
  }, [filters]);

  // Query key with normalized params
  const queryKey = useMemo(
    () => queryKeys.discover.list(mediaType, searchQuery || null, discoverParams),
    [mediaType, searchQuery, discoverParams]
  );

  // Infinite query for discover/search
  const {
    data,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 1 }): Promise<MediaDiscoverResponse> => {
      if (searchQuery) {
        return searchMedia(searchQuery, pageParam, mediaType);
      }
      return discoverMedia(apiFilters, pageParam, mediaType);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      return nextPage <= lastPage.total_pages ? nextPage : undefined;
    },
    staleTime: 60_000, // 60 seconds
    gcTime: 30 * 60_000, // 30 minutes
  });

  // Flatten pages into single array with deduplication
  const rawMovies = useMemo(() => {
    if (!data?.pages) return [];
    const seen = new Set<number>();
    const items: MediaItem[] = [];
    for (const page of data.pages) {
      for (const item of page.results) {
        if (!seen.has(item.id)) {
          seen.add(item.id);
          items.push(item);
        }
      }
    }
    return items;
  }, [data?.pages]);

  // Get total results from first page
  const totalResults = data?.pages[0]?.total_results ?? 0;

  // Apply client-side filters and sorting (no bespoke runtime cache - use API payload only)
  const movies = useMemo(() => {
    let filtered = applyClientFilters(rawMovies, filters);

    if (CLIENT_SORT_OPTIONS.includes(filters.sortBy)) {
      return sortMedia(filtered, filters.sortBy);
    }
    return filtered;
  }, [rawMovies, filters]);

  // Auto-load more when filtered results are low
  const hasRatingFilters =
    filters.ratingRange[0] > 0 || filters.ratingRange[1] < 10 ||
    filters.rtRatingRange[0] > 0 || filters.rtRatingRange[1] < 100 ||
    filters.tmdbRatingRange[0] > 0 || filters.tmdbRatingRange[1] < 10 ||
    filters.ourRatingRange[0] > 0 || filters.ourRatingRange[1] < 10;

  const autoLoadCountRef = useRef(0);

  // Reset auto-load counter when filters change
  useEffect(() => {
    autoLoadCountRef.current = 0;
  }, [filters, searchQuery]);

  useEffect(() => {
    const MIN_VISIBLE_ITEMS = 12;
    const MAX_AUTO_LOADS = 3;

    if (
      hasRatingFilters &&
      movies.length < MIN_VISIBLE_ITEMS &&
      hasNextPage &&
      !isFetching &&
      !isFetchingNextPage &&
      autoLoadCountRef.current < MAX_AUTO_LOADS
    ) {
      autoLoadCountRef.current += 1;
      fetchNextPage();
    }
  }, [movies.length, hasRatingFilters, hasNextPage, isFetching, isFetchingNextPage, fetchNextPage]);

  // Handle media type changes - reset genres
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    if (mediaType !== prevMediaTypeRef.current) {
      prevMediaTypeRef.current = mediaType;
      // Reset genre and runtime filters when switching media type
      // (genre IDs differ; runtime presets differ between movie and TV)
      setFilters(prev => ({
        ...prev,
        genres: [],
        runtimeRange: [0, 300],
      }));
    }
  }, [mediaType]);

  // Load more callback
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetching && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetching, isFetchingNextPage, fetchNextPage]);

  return {
    movies,
    // Include isFetchingNextPage in loading so skeletons show during pagination
    loading: isLoading || isFetchingNextPage,
    isFetching,
    isFetchingNextPage,
    error: error ? 'Failed to fetch content' : null,
    hasMore: !!hasNextPage,
    totalResults,
    loadMore,
    filters,
    setFilters,
    searchQuery,
    setSearchQuery,
  };
}
